/**
 * 相册详情页
 * 瀑布流展示照片 + Lightbox 大图浏览
 */
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAlbum, type Album, type Photo } from '../src/api/albums';

const AlbumDetailPage: React.FC = () => {
  const { albumId } = useParams<{ albumId: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isZh = i18n.language === 'zh';

  const [album, setAlbum] = useState<Album | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    if (albumId) loadAlbum(albumId);
  }, [albumId]);

  const loadAlbum = async (id: string) => {
    try {
      setIsLoading(true);
      const data = await getAlbum(id);
      setAlbum(data);
      const title = isZh ? data.title : (data.titleEn || data.title);
      document.title = `${title} - ${t('gallery.title')} - ${t('html.title')}`;
    } catch (err) {
      console.error('Failed to load album:', err);
      navigate('/gallery');
    } finally {
      setIsLoading(false);
    }
  };

  const getApiBaseUrl = () => {
    return import.meta.env.VITE_NODE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8084';
  };

  const getImageUrl = (path: string) => {
    if (path.startsWith('http')) return path;
    return `${getApiBaseUrl()}${path}`;
  };

  // Lightbox 导航
  const photos = album?.photos || [];

  const handlePrev = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex(lightboxIndex > 0 ? lightboxIndex - 1 : photos.length - 1);
  }, [lightboxIndex, photos.length]);

  const handleNext = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex(lightboxIndex < photos.length - 1 ? lightboxIndex + 1 : 0);
  }, [lightboxIndex, photos.length]);

  // 键盘导航
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, handlePrev, handleNext]);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!album) return null;

  const albumTitle = isZh ? album.title : (album.titleEn || album.title);

  return (
    <div className="bg-white min-h-screen">
      {/* 头部 */}
      <section className="pt-24 pb-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <button
            onClick={() => navigate('/gallery')}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 mb-4 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('gallery.backToGallery')}
          </button>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-2">
            {albumTitle}
          </h1>
          {album.description && (
            <p className="text-lg text-slate-500">
              {isZh ? album.description : (album.descriptionEn || album.description)}
            </p>
          )}
          <div className="flex items-center gap-3 mt-3 text-sm text-slate-500">
            <span>{photos.length} {t('gallery.photos')}</span>
            {album.tags?.map(tag => (
              <span key={tag} className="px-2 py-0.5 bg-white/80 rounded text-xs">{tag}</span>
            ))}
          </div>
        </div>
      </section>

      {/* 瀑布流照片 */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        {photos.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            {t('gallery.noPhotos')}
          </div>
        ) : (
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                className="break-inside-avoid cursor-pointer group"
                onClick={() => setLightboxIndex(index)}
              >
                <div className="rounded-xl overflow-hidden shadow-sm group-hover:shadow-lg transition-shadow">
                  <img
                    src={getImageUrl(photo.thumbUrl)}
                    alt={isZh ? (photo.title || '') : (photo.titleEn || photo.title || '')}
                    className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-300"
                    loading="lazy"
                    style={{
                      aspectRatio: photo.width && photo.height ? `${photo.width}/${photo.height}` : undefined,
                    }}
                  />
                </div>
                {photo.title && (
                  <p className="text-xs text-slate-500 mt-1.5 px-1">
                    {isZh ? photo.title : (photo.titleEn || photo.title)}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && photos[lightboxIndex] && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            backgroundColor: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setLightboxIndex(null)}
        >
          {/* 关闭按钮 */}
          <button
            onClick={() => setLightboxIndex(null)}
            style={{ position: 'absolute', top: 16, right: 16, padding: 8, color: 'rgba(255,255,255,0.7)', cursor: 'pointer', background: 'none', border: 'none' }}
          >
            <svg width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* 上一张 */}
          <button
            onClick={(e) => { e.stopPropagation(); handlePrev(); }}
            style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', padding: 12, color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.1)', borderRadius: '50%', border: 'none', cursor: 'pointer' }}
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* 图片 */}
          <img
            src={getImageUrl(photos[lightboxIndex].url)}
            alt={photos[lightboxIndex].title || ''}
            style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain' }}
            onClick={(e) => e.stopPropagation()}
          />

          {/* 下一张 */}
          <button
            onClick={(e) => { e.stopPropagation(); handleNext(); }}
            style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', padding: 12, color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.1)', borderRadius: '50%', border: 'none', cursor: 'pointer' }}
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* 底部信息 */}
          <div style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
            <p style={{ fontSize: 14 }}>
              {lightboxIndex + 1} / {photos.length}
            </p>
            {photos[lightboxIndex].title && (
              <p style={{ fontSize: 14, marginTop: 4 }}>
                {isZh ? photos[lightboxIndex].title : (photos[lightboxIndex].titleEn || photos[lightboxIndex].title)}
              </p>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AlbumDetailPage;
